import { useTranslation } from 'react-i18next';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { StepIndicator } from './StepIndicator';
import { VARIATION_TYPES } from './constants';
import type { VariationType } from '@/lib/types';

interface CreateGroupStepProps {
  newGroupName: string;
  newGroupDescription: string;
  newGroupVariationType: VariationType;
  isCreating: boolean;
  variationType: VariationType;
  onSetNewGroupName: (name: string) => void;
  onSetNewGroupDescription: (description: string) => void;
  onSetNewGroupVariationType: (type: VariationType) => void;
  onBack: () => void;
  onCreate: () => void;
}

export function CreateGroupStep({
  newGroupName,
  newGroupDescription,
  newGroupVariationType,
  isCreating,
  variationType,
  onSetNewGroupName,
  onSetNewGroupDescription,
  onSetNewGroupVariationType,
  onBack,
  onCreate,
}: CreateGroupStepProps) {
  const { t } = useTranslation();

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('associations.createNewGroup')}</DialogTitle>
        <DialogDescription>{t('associations.createGroupDescription')}</DialogDescription>
      </DialogHeader>

      <StepIndicator step="create_group" variationType={variationType} />

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="groupName">{t('associations.groupName')} *</Label>
          <Input
            id="groupName"
            value={newGroupName}
            onChange={(e) => onSetNewGroupName(e.target.value)}
            placeholder={t('associations.groupNamePlaceholder')}
            maxLength={50}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="groupDescription">{t('associations.description')}</Label>
          <Input
            id="groupDescription"
            value={newGroupDescription}
            onChange={(e) => onSetNewGroupDescription(e.target.value)}
            placeholder={t('associations.descriptionPlaceholder')}
            maxLength={200}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('associations.variationType')}</Label>
          <Select
            value={newGroupVariationType}
            onValueChange={(value: VariationType) => onSetNewGroupVariationType(value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VARIATION_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center gap-2">
                    {type.icon}
                    {t(type.labelKey)}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="size-4 mr-1" />
          {t('common.back')}
        </Button>
        <Button onClick={onCreate} disabled={isCreating || !newGroupName.trim()}>
          {isCreating && <Loader2 className="size-4 mr-2 animate-spin" />}
          {t('common.create')}
        </Button>
      </DialogFooter>
    </>
  );
}
